import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { FieldError } from "@/components/ui/field";
import { CheckCircle2, Loader2, PhoneCall, XCircle } from "lucide-react";
import PhoneInput, { isValidPhoneNumber, type Value } from "react-phone-number-input";
import {
  useCancelBookingApprovalMutation,
  useCreateBookingApprovalMutation,
  useGetBookingApprovalModeQuery,
  useGetBookingApprovalQuery,
  useVerifyBookingApprovalMutation
} from "@/app/booking-flow/publicBookingFlowApi";
import type {
  BookingApprovalAction,
  BookingApprovalChannelMode,
  BookingApprovalStatus,
  BookingDetails
} from "@/app/booking-flow/publicBookingFlowTypes";
import { isPhoneApprovalPending } from "@/app/booking-flow/publicBookingFlowTypes";
import {
  ApprovalCodeEntry,
  readVerifyFailure,
  type ApprovalCodeError
} from "@/app/booking-flow/components/ApprovalCodeEntry";
import { formatCheckInDateTime } from "../booking-flow/checkInTime";
import { ManagedBookingCard } from "./ManagedBookingCard";

const LOCKED_OUT_MESSAGE =
  "Too many attempts. Please wait a few minutes and try again.";
const NOT_FOUND_MESSAGE =
  "We couldn't find an appointment with that phone number and confirmation number. Please check both and try again.";
const SSN_NOT_FOUND_MESSAGE =
  "We couldn't find an upcoming appointment with that phone number and Social Security Number. Both have to match what was given when the appointment was booked. Please check them and try again, or call us for help.";
const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

// Which of the two things the person is identifying their appointment with. "ssn" is the recovery
// path — it stands in for a confirmation number nobody can find, and is not a way around anything
// else: the phone number still has to be the one on the booking, and with the server's phone
// challenge on that handset is still what authorizes the view or the cancellation.
type CredentialMode = "code" | "ssn";

// 429 is the per-session attempt cap and is worth its own message, since retrying immediately
// cannot help.
//
// 404 is only reachable with the server's phone challenge switched off, where the phone number and
// confirmation number are the whole authorization and so "no such appointment" is a thing this page
// is allowed to say. With the challenge on the endpoint answers 202 for a right code and a wrong one
// alike, and a wrong code instead looks exactly like a call nobody answered — which is what stops
// this page being used to find out which codes are real.
function errorMessageFor(error: unknown, mode: CredentialMode): string {
  const status = (error as { status?: number })?.status;
  if (status === 429) return LOCKED_OUT_MESSAGE;
  if (status === 404) return mode === "ssn" ? SSN_NOT_FOUND_MESSAGE : NOT_FOUND_MESSAGE;
  return GENERIC_ERROR_MESSAGE;
}

function formatCountdown(msRemaining: number): string {
  const total = Math.max(0, Math.ceil(msRemaining / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export default function BookingManagePage() {
  const [createApproval, { isLoading: creating }] = useCreateBookingApprovalMutation();
  const [cancelApproval] = useCancelBookingApprovalMutation();
  const [verifyApproval, { isLoading: verifying }] = useVerifyBookingApprovalMutation();

  const [phoneInput, setPhoneInput] = useState<Value | undefined>();
  const [confirmationInput, setConfirmationInput] = useState("");
  const [ssnInput, setSsnInput] = useState("");
  const [reasonInput, setReasonInput] = useState("");

  const [phoneTouched, setPhoneTouched] = useState(false);
  const [ssnTouched, setSsnTouched] = useState(false);
  const [formError, setFormError] = useState("");

  // Sticky for the whole visit rather than only for the lookup, because the cancel button submits
  // the credential a second time: somebody who got to their appointment without a confirmation
  // number does not suddenly have one when they decide to cancel it.
  const [credentialMode, setCredentialMode] = useState<CredentialMode>("code");

  const [approvalId, setApprovalId] = useState<string | null>(null);
  const [approvalAction, setApprovalAction] = useState<BookingApprovalAction | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [pollSeconds, setPollSeconds] = useState(3);

  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [customerNote, setCustomerNote] = useState<string | null>(null);
  const [cancelledAt, setCancelledAt] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // Whether this server rings the phone. Asked for up front so the copy can warn about the call
  // before the person commits to it — someone who was not told to expect an unknown number does not
  // answer it — and then overwritten by what the request actually did, which is authoritative and
  // cannot desync if the flag moves mid-visit. Undefined only while that first read is in flight.
  const { data: mode } = useGetBookingApprovalModeQuery();
  const [observedChallenge, setObservedChallenge] = useState<boolean | null>(null);
  const phoneChallenge = observedChallenge ?? mode?.phoneApprovalRequired;

  // Call or one-time code (AB#585), the same way round: the mode query for copy before the first
  // request, then whatever the last request's 202 said, which also decides whether to poll.
  const [observedApprovalMode, setObservedApprovalMode] = useState<BookingApprovalChannelMode | null>(null);
  const approvalMode: BookingApprovalChannelMode = observedApprovalMode ?? mode?.approvalMode ?? "call";
  const codeMode = approvalMode === "code";

  // Code mode's stand-in for the poll's answer: what the verify endpoint returned (the same
  // BookingApprovalStatus shape), or a local "expired" for a 410. Fed to the same effect below.
  const [verifiedApproval, setVerifiedApproval] = useState<BookingApprovalStatus | null>(null);
  const [codeError, setCodeError] = useState<ApprovalCodeError | undefined>(undefined);

  // Ticks once a second purely so the countdown renders and so the local expiry guard below has
  // something to re-evaluate against.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (approvalId === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [approvalId]);

  // Two independent stopping conditions, and both are needed. The server's state is authoritative,
  // but a wedged or unreachable backend would otherwise leave this spinning forever, so the window
  // the server already told us about is enforced here too.
  const locallyExpired = expiresAt !== null && now > expiresAt;

  // RTK Query has no "stop after this response" hook, so polling is stopped by taking the interval
  // to zero. Held as state rather than derived from `approval`, which does not exist yet on the
  // line that configures the query.
  const [polling, setPolling] = useState(false);

  // Skipped outright in code mode: there is nothing to poll, and even one read would be a wasted request.
  const { data: polledApproval, isError: approvalError } = useGetBookingApprovalQuery(
    approvalId !== null && !codeMode ? approvalId : skipToken,
    {
      pollingInterval: polling && !locallyExpired ? pollSeconds * 1000 : 0,
      skipPollingIfUnfocused: false
    }
  );
  const approval = codeMode ? verifiedApproval ?? undefined : polledApproval;

  // A 404 after the approval was readable means the record has simply aged out of the cache, which
  // is an expiry rather than a fault worth apologising for.
  const state = approvalError || locallyExpired ? "expired" : approval?.state ?? "pending";

  useEffect(() => {
    if (!approval || approval.state === "pending") return;
    setPolling(false);

    if (approval.state !== "approved") return;

    if (approvalAction === "ViewBooking" && approval.booking) {
      showBooking(approval.booking, approval.customerNote);
      setApprovalId(null);
    } else if (approvalAction === "CancelBooking" && approval.cancelledAt) {
      setCancelledAt(approval.cancelledAt);
      setApprovalId(null);
    }
  }, [approval, approvalAction]);

  // `now` is also what the booking card judges "has this appointment passed" against, so it is
  // refreshed at the moment the booking arrives rather than left wherever the countdown stopped.
  function showBooking(found: BookingDetails, note: string | null | undefined) {
    setNow(Date.now());
    setCustomerNote(note ?? null);
    setBooking(found);
  }

  const phoneValid = !!phoneInput && isValidPhoneNumber(phoneInput);
  const ssnValid = /^\d{9}$/.test(ssnInput);
  const canSubmit = phoneValid && (credentialMode === "ssn" ? ssnValid : confirmationInput.trim() !== "");

  const requestApproval = async (action: BookingApprovalAction): Promise<boolean> => {
    setFormError("");

    try {
      const result = await createApproval({
        phoneNumber: phoneInput!,
        // One or the other, never both — the endpoint refuses a request carrying two credentials.
        ...(credentialMode === "ssn" ? { ssn: ssnInput } : { confirmationCode: confirmationInput.trim() }),
        action,
        cancellationReason: action === "CancelBooking" && reasonInput.trim() !== "" ? reasonInput.trim() : undefined
      }).unwrap();

      // No phone challenge configured: the server has already done it, and there is no approval to
      // watch. Applied straight to the same state the poll would have set, so everything below this
      // renders identically either way.
      if (!isPhoneApprovalPending(result)) {
        setObservedChallenge(false);
        if (action === "ViewBooking" && result.booking) {
          showBooking(result.booking, result.customerNote);
        } else if (action === "CancelBooking" && result.cancelledAt) {
          setCancelledAt(result.cancelledAt);
        }
        return true;
      }

      setObservedChallenge(true);
      // A server without the mode field only ever rings the phone.
      const sentCode = result.mode === "code";
      setObservedApprovalMode(sentCode ? "code" : "call");
      setVerifiedApproval(null);
      setCodeError(undefined);
      setApprovalAction(action);
      setExpiresAt(Date.parse(result.expiresAt));
      setPollSeconds(result.pollIntervalSeconds);
      setNow(Date.now());
      setPolling(!sentCode);
      setApprovalId(result.approvalId);
      return true;
    } catch (error) {
      setFormError(errorMessageFor(error, credentialMode));
      return false;
    }
  };

  // Code mode: checks the typed code. A 200 is handled exactly as a poll's answer would be; a wrong
  // code stays on this view with the guesses left; an approval that is over (410, or the last guess
  // used) shows the expired view; a 409 means approvals went back to calls, so the call view takes over.
  const verifyCode = async (code: string) => {
    if (approvalId === null) return;
    setCodeError(undefined);
    try {
      setVerifiedApproval(await verifyApproval({ approvalId, code }).unwrap());
    } catch (error) {
      const failure = readVerifyFailure(error);
      if (failure.kind === "rejected") {
        setCodeError(failure);
      } else if (failure.kind === "gone") {
        setVerifiedApproval({ approvalId, state: "expired", expiresAt: new Date(expiresAt ?? Date.now()).toISOString() });
      } else if (failure.kind === "notCodeMode") {
        setObservedApprovalMode("call");
        setPolling(true);
      } else {
        setCodeError(failure);
      }
    }
  };

  // "Send a new code": the same request again, as the call view's retry would make it, then the old
  // approval withdrawn (the new one has already superseded it; this only tidies up).
  const resendCode = async () => {
    const previous = approvalId;
    if (approvalAction === null) return;
    setCodeError(undefined);
    if (await requestApproval(approvalAction) && previous !== null) {
      void cancelApproval(previous);
    }
  };

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneValid) {
      setPhoneTouched(true);
      return;
    }
    if (credentialMode === "ssn" && !ssnValid) {
      setSsnTouched(true);
      return;
    }
    await requestApproval("ViewBooking");
  };

  // Switching credential drops whatever was typed for the other one, along with an error that was
  // about it. Nothing is submitted here — the person still has to press the button — so this costs
  // no attempt against the session's budget.
  const handleCredentialModeChange = (mode: CredentialMode) => {
    setCredentialMode(mode);
    setFormError("");
    setSsnTouched(false);
    if (mode === "ssn") {
      setConfirmationInput("");
    } else {
      setSsnInput("");
    }
  };

  const handleCancel = async (e: React.FormEvent) => {
    e.preventDefault();
    // Left open on failure so the reason survives and the error has somewhere to render; the
    // booking card behind it is unchanged either way.
    if (await requestApproval("CancelBooking")) {
      setCancelDialogOpen(false);
    }
  };

  // The error belongs to the attempt that produced it, not to the next one, and the booking card
  // no longer shows it once the dialog owns the cancel flow.
  const handleCancelDialogChange = (open: boolean) => {
    if (creating) return;
    if (open) setFormError("");
    setCancelDialogOpen(open);
  };

  // Stopping the poll is the easy half. The half that matters is telling the server, because the
  // approval goes out as a phone call and one that rings out is tried again: someone who typed the
  // wrong number and clicked this would otherwise have a stranger's phone ring a second time for a
  // request nobody is waiting on. Withdrawing takes the approval terminal, and every re-attempt
  // checks that first.
  const clearApproval = () => {
    const withdrawing = approvalId;

    setPolling(false);
    setApprovalId(null);
    setApprovalAction(null);
    setExpiresAt(null);
    setVerifiedApproval(null);
    setCodeError(undefined);

    // Not awaited, and failure is swallowed on purpose. The page has already moved on, there is
    // nothing the person could usefully do about a failed withdrawal, and an approval nobody
    // withdraws still lapses on its own a few minutes later.
    if (withdrawing !== null) {
      void cancelApproval(withdrawing);
    }
  };

  const handleStartOver = () => {
    clearApproval();
    setBooking(null);
    setCustomerNote(null);
    setCancelDialogOpen(false);
    setReasonInput("");
    setFormError("");
  };

  const waiting = approvalId !== null && state === "pending";
  const resolvedAgainstUs = approvalId !== null && (state === "rejected" || state === "expired" || state === "failed");

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col">
      <PublicHeader />

      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        {/* Each view is keyed so switching between them mounts fresh DOM. Unkeyed, React reuses the
            footer's <button> elements by position: the booking view's Back button became the lookup
            form's submit button mid-click, and the browser's click activation then submitted the
            still-filled lookup form, landing straight back on the booking view. */}
        {cancelledAt ? (
          <Card key="cancelled" className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4 space-y-4">
              <div className="mx-auto w-16 h-16 bg-emerald-100 flex items-center justify-center rounded-full">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl font-semibold text-slate-900">Appointment Cancelled</CardTitle>
              <CardDescription className="text-slate-500 text-base">
                If you need energy assistance, please schedule a new appointment when the next set of appointments becomes available.
              </CardDescription>
            </CardHeader>

            <CardFooter className="px-4 pb-6">
              <Button asChild variant="ghost" className="w-full text-slate-400">
                <Link to="/booking">Back to Scheduler Home</Link>
              </Button>
            </CardFooter>
          </Card>
        ) : waiting && codeMode ? (
          <ApprovalCodeEntry
            key={`code:${approvalId}`}
            className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm"
            action={approvalAction ?? "ViewBooking"}
            expiresAt={expiresAt}
            nowMs={now}
            onVerify={verifyCode}
            onResend={() => void resendCode()}
            onStartOver={clearApproval}
            error={codeError ?? (formError !== "" ? { kind: "other", message: formError } : undefined)}
            busy={verifying || creating}
          />
        ) : waiting ? (
          <Card key="waiting" className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4 space-y-4">
              <div className="mx-auto w-16 h-16 bg-sky-100 flex items-center justify-center rounded-full">
                <PhoneCall className="w-8 h-8 text-sky-600" />
              </div>
              <CardTitle className="text-2xl font-semibold text-slate-900">Answer Your Phone</CardTitle>
              <CardDescription className="text-slate-500 text-base">
                We're calling the phone number on this appointment. When you answer, press <strong>1</strong> to
                {approvalAction === "CancelBooking" ? " cancel it" : " see your appointment details"}, or{" "}
                <strong>2</strong> to deny.
              </CardDescription>
            </CardHeader>

            <CardContent className="flex flex-col items-center gap-3" aria-live="polite">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              {expiresAt !== null && (
                <p className="text-sm text-slate-500">
                  This request expires in {formatCountdown(expiresAt - now)}
                </p>
              )}
            </CardContent>

            <CardFooter className="px-4 pb-6">
              <Button type="button" variant="ghost" className="w-full text-slate-400" onClick={clearApproval}>
                Retry this request
              </Button>
            </CardFooter>
          </Card>
        ) : resolvedAgainstUs ? (
          <Card key="resolved" className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4 space-y-4">
              <div className="mx-auto w-16 h-16 bg-slate-100 flex items-center justify-center rounded-full">
                <XCircle className="w-8 h-8 text-slate-500" />
              </div>
              <CardTitle className="text-2xl font-semibold text-slate-900">
                {state === "rejected"
                  ? "Request Denied"
                  : state === "failed"
                    ? "Something Went Wrong"
                    : codeMode
                      ? "Code No Longer Valid"
                      : "No Response"}
              </CardTitle>
              <CardDescription className="text-slate-500 text-base">
                {state === "rejected"
                  ? "The request was denied from the phone on this appointment. Nothing was changed."
                  : state === "failed"
                    ? "The request was approved, but we could not finish it. Please call us so we can help."
                    : codeMode
                      ? credentialMode === "ssn"
                        ? "That code can no longer be used. Check that you entered the right phone number and Social Security Number, then try again."
                        : "That code can no longer be used. Check that you entered the right phone number and confirmation number, then try again."
                      : credentialMode === "ssn"
                        ? "We did not get an answer. Check that you entered the right phone number and Social Security Number, then try again."
                        : "We did not get an answer. Check that you entered the right phone number and confirmation number, then try again."}
              </CardDescription>
            </CardHeader>

            <CardFooter className="px-4 pb-6 flex flex-col gap-2">
              <Button
                type="button"
                className="w-full text-base h-12 bg-[#009681] hover:bg-[#007e6c] active:bg-[#006b5b]"
                onClick={handleStartOver}
              >
                Try Again
              </Button>
              <Button asChild variant="ghost" className="w-full text-slate-400">
                <Link to="/booking">Back to Scheduler Home</Link>
              </Button>
            </CardFooter>
          </Card>
        ) : booking ? (
          // Status-aware (AB#624): cancelled and past bookings are shown for what they are, and only
          // an upcoming accepted one gets the Cancel button and the dialog behind it.
          <ManagedBookingCard
            key="booking"
            booking={booking}
            customerNote={customerNote}
            nowMs={now}
            onCancel={() => handleCancelDialogChange(true)}
            onBack={handleStartOver}
          >
            <Dialog open={cancelDialogOpen} onOpenChange={handleCancelDialogChange}>
              <DialogContent className="sm:max-w-md" showCloseButton={!creating}>
                <form onSubmit={handleCancel} className="space-y-4">
                  <DialogHeader>
                    <DialogTitle>Cancel This Appointment?</DialogTitle>
                    <DialogDescription>
                      {formatCheckInDateTime(booking.start)}
                      {phoneChallenge
                        ? codeMode
                          ? " — cancelling sends one more code to your phone to confirm."
                          : " — cancelling places one more call to your phone to confirm."
                        : " — THIS CANNOT BE UNDONE. IF YOU NEED ENERGY ASSISTANCE, YOU SHOULD MAKE A NEW APPOINTMENT WHEN THE NEXT SET OF APPOINTMENTS BECOMES AVAILABLE."}
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-2">
                    <Label htmlFor="reason">Reason for Cancelling</Label>
                    <Textarea
                      id="reason"
                      name="reason"
                      placeholder="Optional"
                      value={reasonInput}
                      onChange={(e) => setReasonInput(e.target.value)}
                    />
                    {formError !== "" && <FieldError>{formError}</FieldError>}
                  </div>

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="ghost"
                      className="text-slate-400"
                      disabled={creating}
                      onClick={() => handleCancelDialogChange(false)}
                    >
                      Keep Appointment
                    </Button>
                    <Button type="submit" variant="destructive" disabled={creating}>
                      {creating ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {phoneChallenge ? (codeMode ? "Sending code…" : "Calling…") : "Cancelling…"}
                        </span>
                      ) : (
                        "Submit Cancellation"
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </ManagedBookingCard>
        ) : (
          <Card key="lookup" className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4">
              <CardTitle className="text-2xl font-semibold text-slate-900">Manage Your Appointment</CardTitle>
              <CardDescription className="text-slate-500">
                {credentialMode === "code"
                  ? "Enter the phone number and confirmation number from your booking."
                  : "Enter the phone number on your booking and the Social Security Number it was booked with."}
                {phoneChallenge && " We'll contact that phone to confirm it's you."}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form className="space-y-4" id="booking-lookup-form" onSubmit={handleLookup}>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number <span aria-hidden="true">*</span></Label>
                  <PhoneInput
                    id="phone"
                    name="phone"
                    placeholder="Enter phone number"
                    defaultCountry="US"
                    value={phoneInput}
                    onChange={(value) => setPhoneInput(value)}
                    onBlur={() => setPhoneTouched(true)}
                    className="border-input dark:bg-input/30 [&.PhoneInput--focus]:border-ring [&.PhoneInput--focus]:ring-ring/50 [&.PhoneInput--focus]:ring-[3px] flex h-9 w-full min-w-0 items-center gap-2 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm"
                    numberInputProps={{
                      className: "placeholder:text-muted-foreground min-w-0 flex-1 border-0 bg-transparent p-0 text-base shadow-none outline-none md:text-sm",
                    }}
                  />
                  {phoneTouched && !phoneValid && (
                    <FieldError>Enter a valid phone number.</FieldError>
                  )}
                </div>

                {credentialMode === "code" ? (
                  <div className="space-y-2">
                    <Label htmlFor="confirmationNumber">Confirmation Number <span aria-hidden="true">*</span></Label>
                    <Input
                      id="confirmationNumber"
                      name="confirmationNumber"
                      type="text"
                      inputMode="numeric"
                      placeholder="6-digit confirmation number"
                      value={confirmationInput}
                      onChange={(e) => setConfirmationInput(e.target.value)}
                      required
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="ssn">Social Security Number <span aria-hidden="true">*</span></Label>
                    <p id="manage-ssn-note" className="text-sm text-slate-500">
                      Enter the 9 digit Social Security Number given when the appointment was booked. It has to be the
                      one on the appointment, and the phone number above has to be the one we have on file for it.
                    </p>
                    <Input
                      id="ssn"
                      name="ssn"
                      type="password"
                      // Without this, a type="password" field with no autocomplete hint prompts the
                      // browser to save the value to the password manager.
                      autoComplete="off"
                      inputMode="numeric"
                      pattern="\d{9}"
                      maxLength={9}
                      placeholder="•••••••••"
                      value={ssnInput}
                      onChange={(e) => setSsnInput(e.target.value.replace(/\D/g, "").slice(0, 9))}
                      onBlur={() => setSsnTouched(true)}
                      aria-describedby="manage-ssn-note"
                      aria-invalid={ssnTouched && !ssnValid}
                      required
                    />
                    {ssnTouched && !ssnValid && (
                      <FieldError>Social Security Number must be 9 digits.</FieldError>
                    )}
                  </div>
                )}

                {formError !== "" && <FieldError>{formError}</FieldError>}
              </form>
            </CardContent>

            <CardFooter className="px-4 pb-6 flex flex-col gap-2">
              <Button
                type="button"
                variant="ghost"
                className="w-full text-slate-500"
                onClick={() => handleCredentialModeChange(credentialMode === "code" ? "ssn" : "code")}
              >
                {credentialMode === "code"
                  ? "I forgot my confirmation number"
                  : "Use my confirmation number instead"}
              </Button>
              <Button
                form="booking-lookup-form"
                type="submit"
                className="w-full text-base h-12 bg-[#009681] hover:bg-[#007e6c] active:bg-[#006b5b]"
                disabled={creating || !canSubmit}
              >
                {creating ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {phoneChallenge ? (codeMode ? "Sending code…" : "Calling…") : "Loading…"}
                  </span>
                ) : (
                  "View Appointment"
                )}
              </Button>
              <Button asChild variant="ghost" className="w-full text-slate-400">
                <Link to="/booking">Back to Scheduler Home</Link>
              </Button>
            </CardFooter>
          </Card>
        )}
      </main>

      <PublicFooter>Multi-Service Center &nbsp;·&nbsp; Energy Assistance</PublicFooter>
    </div>
  );
}
